import { find, maxBy } from '@seiue/util'
import { PaperQuestionWithReq } from './types'
import { AnyValueObj } from 'packages/types'
import {
  PaperQuestion,
  PaperQuestionReq,
  QuestionTypeEnum,
} from 'packages/sdks-next/growp'

/**
 * 获取编辑试卷时的默认值
 *
 * @param questions - 试卷题目
 * @returns 试卷题目
 */
export const getEditQuestionDefaultValue: (
  questions: PaperQuestion[] | undefined
) => PaperQuestionReq = (questions: PaperQuestion[] | undefined) => {
  // 主题干数据
  const mainQuestion = questions?.find((item) => item.parentId === 0)

  if (!mainQuestion) {
    return {
      type: QuestionTypeEnum.SingleChoice,
      options: ['A', 'B', 'C', 'D'],
      standardAnswers: [],
      parentId: 0,
      treeNodeId: 1,
      treeNodePid: 0,
    }
  }
  return mainQuestion
}

// 获取主题目之外的questions并按照weight排序
export const getInitSubQuestions = (questions: PaperQuestionWithReq[]) => {
  return questions
    .filter((question) => question.id && question.parentId !== 0)
    .sort(
      (questionA, questionB) =>
        (questionA.weight || 0) - (questionB.weight || 0)
    )
    .map((item) => {
      return {
        ...item,
        treeNodeId: item.treeNodeId || item.id,
        treeNodePid: item.treeNodePid || item.parentId,
      }
    })
}

/**
 * 格式化填空题子题 - 设置treeNodeId和weight
 * 保证treeNodeId唯一
 */
export const formatFillInBlankQuestion = (
  subQuestions: PaperQuestionWithReq[]
) => {
  return subQuestions.map((i, index) => ({
    ...i,
    // treeNodeId: index + 2,
    weight: index + 2,
  }))
}

/**
 * 生成treeNodeId
 * @param questions - 已经存在的问题
 * @returns 新生成的treeNodeId
 */
export const getTreeNodeId = (questions: PaperQuestionWithReq[]) => {
  if (!questions.length) {
    return 2
  }
  return (
    (maxBy(questions, (question) => question.treeNodeId)?.treeNodeId || 0) + 1
  )
}

/**
 * 计算当前题目的总分
 * @param subQuestions - 当前问题的所有子题
 * @returns 计算后的总分
 */
export const computedFillInBlankSumScore = (
  subQuestions: PaperQuestionWithReq[]
) => {
  const flag = subQuestions.every((q) => !q.score)
  if (flag) {
    return ''
  }
  let sumScore = 0
  subQuestions.map((i) => {
    sumScore += i.score ? Number(i.score) : 0
  })
  return sumScore
}

/**
 * 根据parentId获取子题
 */
export const getSubQuestions = (
  subQuestions: PaperQuestionWithReq[],
  parentId: number
) => {
  return subQuestions
    .filter((question) => question.parentId === parentId)
    .sort((qa, qb) => (qa.weight || 0) - (qb.weight || 0))
}

/**
 * 删除子题
 * @param param.subQuestions - 所有子题
 * @param param.treeNodeId - 要删除子题的treeNodeId
 * @returns 删除后剩下的子题
 */
export const deleteSubQuestions = (
  subQuestions: PaperQuestionWithReq[],
  treeNodeId: number
) => {
  const newSubQuestions: PaperQuestionWithReq[] = []
  subQuestions.map((question) => {
    if (
      question.treeNodeId !== treeNodeId &&
      question.treeNodePid !== treeNodeId
    ) {
      newSubQuestions.push(question)
    }
  })
  return newSubQuestions
}

/**
 * 编辑复合题型 - 题干|解析|分数...
 * @param subQuestions
 * @param treeNodeId
 * @param newParam
 */
export const editSubQuestion = (
  subQuestions: PaperQuestionWithReq[],
  treeNodeId: number,
  newParam: AnyValueObj
) => {
  const newQuestions = subQuestions.map((question) => {
    if (question.treeNodeId === treeNodeId) {
      return {
        ...question,
        ...newParam,
      }
    } else {
      return question
    }
  })
  return newQuestions
}

/**
 * 校验填空题答案是否通过校验
 * @param subQuestions - 填空题子题
 * @param showScore - 是否开启了分数显示
 * @returns boolean
 */
export const validateFillInBlank = (
  subQuestions: PaperQuestionWithReq[],
  showScore: boolean
) => {
  let flag = true
  if (subQuestions.length < 1) {
    flag = false
  }
  subQuestions.map((q) => {
    if (showScore && !q.score) {
      flag = false
    }
    if (
      !q.standardAnswers ||
      q.standardAnswers.length < 1 ||
      q.standardAnswers.includes('')
    ) {
      flag = false
    }
  })
  return flag
}

/**
 * 校验复合必填项是否全部填写
 */
export const validateProductRequired = (
  subQuestions: PaperQuestionWithReq[],
  allSubQuestions: PaperQuestionWithReq[],
  showScore: boolean
) => {
  const questionNums: number[] = []
  subQuestions.map((subQuestion, index) => {
    const { type, analysis, standardAnswers, score } = subQuestion
    if (type === QuestionTypeEnum.FillInBlank) {
      const questions = getSubQuestions(
        allSubQuestions,
        subQuestion.id || subQuestion.treeNodeId || 0
      )
      // 填空题答案选项为0，则无法通过校验
      if (questions.length === 0) {
        !questionNums.includes(index + 1) && questionNums.push(index + 1)
      }
      questions.map((q) => {
        if (showScore && !q.score) {
          !questionNums.includes(index + 1) && questionNums.push(index + 1)
        }
        if (
          !q.standardAnswers ||
          q.standardAnswers.length < 1 ||
          q.standardAnswers.includes('')
        ) {
          !questionNums.includes(index + 1) && questionNums.push(index + 1)
        }
      })
    } else {
      if (
        !type ||
        !analysis ||
        !standardAnswers ||
        (showScore && !score) ||
        standardAnswers.length < 1 ||
        (standardAnswers.length < 2 && QuestionTypeEnum.MultipleChoice === type)
      ) {
        !questionNums.includes(index + 1) && questionNums.push(index + 1)
      }
    }
  })
  return questionNums
}

export const getChildrenById = (allQuestions: PaperQuestion[], id: number) => {
  find(allQuestions, { parentId: id })
}
